import * as Sentry from '@sentry/vue';
import { shouldDropBookProcessedSentryEvent } from '@/utilities/bookProcessedError';
import {
    BROWSER_EXTENSION_URL_PATTERNS,
    shouldDropBrowserExtensionSentryEvent,
} from '@/utilities/browserExtensionNoise';

function createSentry({ app, router }) {
    // only run Sentry in deployed environments
    if (
        import.meta.env.VITE_ENV == 'production' ||
        import.meta.env.VITE_ENV == 'testing' ||
        import.meta.env.VITE_ENV == 'development'
    ) {
        let reportingEnvironment = import.meta.env.VITE_RELEASE_BRANCH ?? import.meta.env.VITE_ENV;

        if (reportingEnvironment == 'main') {
            reportingEnvironment = 'production';
        }

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            release: import.meta.env.VITE_RELEASE ? `movepro-book@${import.meta.env.VITE_RELEASE}` : undefined,
            environment: reportingEnvironment,
            integrations: [
                Sentry.browserTracingIntegration({ router }),
                Sentry.replayIntegration({
                    networkDetailAllowUrls: [
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/orders/[A-Z0-9]+/inventory'),
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/[A-Z0-9]+/trigger'),
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/token'),
                        new RegExp(import.meta.env.VITE_API_BASE_URL + '/customers/book/[A-Z0-9+]'),
                    ],
                    networkRequestHeaders: ['X-Custom-Header'],
                    networkResponseHeaders: ['X-Custom-Header'],
                }),
            ],

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: 0.1,

            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            replaysSessionSampleRate: 0.01,
            replaysOnErrorSampleRate: 1.0,

            ignoreErrors: [
                // Stripe Radar/hCaptcha posts to a child iframe via setTimeout; when the
                // frame is gone (backgrounded PaymentPage / unmounted element) Stripe
                // throws and Sentry captures it as unhandled. Card checkout still works.
                // MOVEPRO-BOOK-AA / CU-86d3whqfy
                'frame window is not ready',
                // Stripe Payment Element wallet probe from Stripe's own cross-origin
                // iframe (ApplePaySession.supportsVersion). Stripe sets allow="payment"
                // on that frame and WebKit throws anyway, intermittently, on both Mobile
                // Safari and Chrome iOS (stripe-js#909). Card checkout is unaffected.
                // MOVEPRO-BOOK-A9 / CU-86d3wg73e
                //
                // Two of our own configurations throw the same message, and this filter
                // would hide both. The `payment` permission has to be delegated at every
                // level of the frame chain, so Stripe's nested frame is denied whenever
                // the Book document itself does not hold it:
                //
                //   1. An embed. Book supports being iframed (?tmpl=0, CU-86d3f1y1v;
                //      live for one tenant) and this origin sends no X-Frame-Options, so
                //      a tenant's iframe without allow="payment" kills the wallets on
                //      every load. Fix: allow="payment" on their iframe.
                //   2. Our own edge headers. A Permissions-Policy of `payment=(self)`
                //      does NOT delegate to js.stripe.com, so it kills the wallets in
                //      every browser. Book needs payment=(self "https://js.stripe.com").
                //      See CASA B1c (CU-86d3xcqwm) before adding a Book block to
                //      infra/cloudfront/security-headers.
                //
                // So a sustained rise here, rather than the odd WebKit event, means one
                // of those two and not Stripe.
                'Third-party iframes are not allowed to request payments unless explicitly allowed via Feature-Policy',
            ],

            // Drop errors whose script URL is a browser extension (PayPal Honey Safari,
            // chrome-/moz-/safari-extension schemes). MOVEPRO-BOOK-AH.
            denyUrls: BROWSER_EXTENSION_URL_PATTERNS,

            // Axios interceptor rejects a stamped plain object after optionally
            // captureException'ing. Uncaught callers still trigger Sentry's
            // onunhandledrejection with that object (MOVEPRO-BOOK-AF "Network error"
            // noise; also BOOK-9Y/9Z). Drop those — the interceptor already decided.
            // Also drop empty `{}` rejections with no stack (MOVEPRO-BOOK-AG) — never
            // from Book's axios path, which always stamps `__bookProcessedError`.
            // Also drop browser-extension frames that slip past denyUrls (MOVEPRO-BOOK-AH,
            // e.g. when only the originalException.stack carries the Honey path).
            beforeSend(event, hint) {
                if (shouldDropBookProcessedSentryEvent(hint)) {
                    return null;
                }
                if (shouldDropBrowserExtensionSentryEvent(event, hint)) {
                    return null;
                }
                return event;
            },
        });
    }
}

/*
 * Set the user context for Sentry
 * @param {string} orderRef - The order reference
 * @param {string} email - The email address
 * @returns {void}
 */
function setUser({ orderRef, email }) {
    if (
        import.meta.env.VITE_ENV == 'production' ||
        import.meta.env.VITE_ENV == 'testing' ||
        import.meta.env.VITE_ENV == 'development'
    ) {
        Sentry.setUser({
            id: orderRef,
            email: email,
        });
    }
}

export { createSentry, setUser };
